// Export chat (like WhatsApp's): the conversation as a text file, or a zip
// with the text and every photo, video, voice note and document (decrypted
// on this device; nothing leaves it).
import { useState } from "preact/hooks";
import { getSession } from "../api";
import { summarize, type Msg } from "../db";
import * as S from "../state";
import { Icon } from "./Icon";
import { engine, toast } from "./Main";
import { decryptedURL } from "./mediaSrc";

const pad = (n: number) => String(n).padStart(2, "0");
const stamp = (ts: number) => {
  const d = new Date(ts);
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}, ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const safe = (s: string) => s.replace(/[\\/:*?"<>|]+/g, "_").slice(0, 80);

function line(m: Msg, me: string, attached?: string) {
  if (m.kind === "system") return `${stamp(m.ts)} - ${m.system ? S.systemText(m.system, m.body) : m.body}`;
  // Your own lines carry your name, like WhatsApp's export.
  const who = m.from === me ? getSession()?.user.name || "You" : S.memberName(m.from);
  const body = m.deleted ? (m.dir === "out" ? "You deleted this message" : "This message was deleted") : attached ? `${attached} (file attached)${m.body ? "\n" + m.body : ""}` : m.body || summarize(m);
  return `${stamp(m.ts)} - ${who}: ${body}`;
}

function save(blob: Blob, name: string) {
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 60_000);
}

export async function exportChat(jid: string, withMedia: boolean, onProgress?: (done: number, total: number) => void) {
  const e = engine.value!;
  const me = e.me;
  const list = (await e.allMessages(jid)).filter((m) => !m.hidden && !m.undecryptable);
  const title = `xmm chat with ${safe(S.displayName(jid))}`;
  const lines: string[] = [];
  const files: Record<string, Uint8Array> = {};
  const media = withMedia ? list.filter((m) => m.media?.url && !m.viewOnce && !m.deleted) : [];
  let done = 0;
  for (const m of list) {
    let attached: string | undefined;
    if (withMedia && media.includes(m)) {
      const ext = (m.media!.name?.match(/\.\w+$/)?.[0] ?? "." + (m.media!.mime.split("/")[1] ?? "bin").replace(/[^\w]/g, "")).slice(0, 8);
      attached = `${m.kind.toUpperCase()}-${new Date(m.ts).toISOString().slice(0, 10).replace(/-/g, "")}-${m.id.slice(0, 6)}${ext}`;
      try {
        const url = m.media!.key ? await decryptedURL(m.media!) : m.media!.url!;
        files[attached] = new Uint8Array(await (await fetch(url)).arrayBuffer());
      } catch {
        attached = undefined; // couldn't fetch it: just the line
      }
      onProgress?.(++done, media.length);
    }
    lines.push(line(m, me, attached));
  }
  const text = new TextEncoder().encode(lines.join("\n") + "\n");
  if (!withMedia) return save(new Blob([text], { type: "text/plain;charset=utf-8" }), title + ".txt");
  const { zipSync } = await import("fflate");
  files[title + ".txt"] = text;
  save(new Blob([zipSync(files, { level: 0 })], { type: "application/zip" }), title + ".zip");
}

/** "Export chat" in contact / group info. */
export function ExportChat({ jid }: { jid: string }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const run = (withMedia: boolean) => {
    setBusy("Preparing…");
    void exportChat(jid, withMedia, (d, t) => setBusy(`Adding media ${d}/${t}…`)).then(
      () => (setBusy(null), setOpen(false), toast("Chat exported")),
      (err) => (setBusy(null), toast("Export failed: " + (err as Error).message)),
    );
  };
  return (
    <>
      <button class="list-action" onClick={() => setOpen(true)}>
        <span class="list-action-icon">
          <Icon name="download" />
        </span>
        Export chat
      </button>
      {open && (
        <div class="modal-backdrop" onClick={() => !busy && setOpen(false)}>
          <div class="modal" role="dialog" aria-label="Export chat" onClick={(e) => e.stopPropagation()}>
            <h2>Export chat</h2>
            <p class="muted small">The chat is saved on this device, readable by anyone you give the file to.</p>
            {busy ? (
              <p>{busy}</p>
            ) : (
              <div class="row-buttons">
                <button class="link" onClick={() => setOpen(false)}>
                  Cancel
                </button>
                <button class="chip" onClick={() => run(false)}>
                  Without media
                </button>
                <button class="primary" onClick={() => run(true)}>
                  Include media
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
